/**
 * DPad — the reusable on-screen 4-way movement control (touch-first). A soft,
 * semi-transparent cross of big press-and-hold targets tucked into the bottom-left,
 * thumb-reachable, respecting the safe-area insets + reduced-motion. Extracted from
 * RouteScene so Town and any other populated overworld can share ONE d-pad instead of
 * re-implementing the button + input-collapse logic.
 *
 * It owns only INPUT: while a direction is held its unit contribution is folded into
 * `vector` (x/y in {-1,0,1}); the owning scene reads `vector` each frame and collapses
 * it to a single cardinal (dominant axis wins) alongside the keyboard. A global
 * pointer-up-outside safety zeroes the vector so a finger released away from a button
 * can never leave an axis stuck.
 *
 * Screen-fixed (scrollFactor 0) so it stays put as the world scrolls. Desktop keyboard
 * walking is unaffected; a scene may hide the pad on a fine-pointer (mouse) device via
 * `isCoarsePointer()` if it would only clutter.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING, TOUCH_TARGET } from './theme.js';

/** A safe-rect the pad seats itself within (matches Layout.safe). */
export interface SafeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DPadOptions {
  /** Skip the press-scale squish (mirrors Settings.reducedMotion). */
  readonly reducedMotion?: boolean;
  /** Render depth for the screen-fixed buttons (default 100). */
  readonly depth?: number;
}

interface PadButton {
  readonly dir: Phaser.Math.Vector2;
  readonly container: Phaser.GameObjects.Container;
  readonly redraw: (pressed: boolean) => void;
  pressed: boolean;
}

/** Button side (px) — the touch-target floor plus a little breathing room. */
const BTN_SIZE = TOUCH_TARGET + SPACING.sm;

export class DPad {
  /** Net held direction (x/y each in {-1,0,1}); the scene collapses this to a cardinal. */
  readonly vector = new Phaser.Math.Vector2(0, 0);

  private readonly scene: Phaser.Scene;
  private readonly reduced: boolean;
  private readonly depth: number;
  private readonly buttons: PadButton[] = [];
  private readonly zeroMove: () => void;
  private visible = true;

  constructor(scene: Phaser.Scene, options: DPadOptions = {}) {
    this.scene = scene;
    this.reduced = options.reducedMotion ?? false;
    this.depth = options.depth ?? 100;

    // order: up, left, right, down (matches the cross layout in layout()).
    const dirs: { dir: [number, number]; glyph: string }[] = [
      { dir: [0, -1], glyph: '▲' },
      { dir: [-1, 0], glyph: '◀' },
      { dir: [1, 0], glyph: '▶' },
      { dir: [0, 1], glyph: '▼' },
    ];
    for (const d of dirs) {
      this.buttons.push(this.makeButton(d.glyph, new Phaser.Math.Vector2(d.dir[0], d.dir[1])));
    }

    // Safety net: any pointer-up OUTSIDE a pad button zeroes the vector + resets visuals,
    // so a finger released away from the pad can never leave an axis stuck.
    this.zeroMove = (): void => this.releaseAll();
    scene.input.on(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.zeroMove);
  }

  private makeButton(glyph: string, dir: Phaser.Math.Vector2): PadButton {
    const size = BTN_SIZE;
    const container = this.scene.add.container(0, 0).setScrollFactor(0).setDepth(this.depth);
    const g = this.scene.add.graphics();
    const t = this.scene.add
      .text(0, 0, glyph, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
      })
      .setOrigin(0.5);
    container.add([g, t]);
    container.setSize(size, size);
    container.setInteractive(
      new Phaser.Geom.Rectangle(-size / 2, -size / 2, size, size),
      Phaser.Geom.Rectangle.Contains,
    );
    container.input!.cursor = 'pointer';

    const redraw = (pressed: boolean): void => {
      g.clear();
      g.fillStyle(pressed ? COLORS.accent : COLORS.surfaceAlt, pressed ? 0.95 : 0.92);
      g.fillRoundedRect(-size / 2, -size / 2, size, size, RADIUS.md);
      g.lineStyle(1.5, pressed ? COLORS.accentDeep : COLORS.border, 1);
      g.strokeRoundedRect(-size / 2, -size / 2, size, size, RADIUS.md);
    };
    redraw(false);

    const btn: PadButton = { dir, container, redraw, pressed: false };

    const press = (): void => {
      if (btn.pressed) return;
      btn.pressed = true;
      this.vector.add(dir);
      this.clampVector();
      if (!this.reduced) container.setScale(0.94);
      redraw(true);
    };
    const release = (): void => {
      if (!btn.pressed) return;
      btn.pressed = false;
      this.vector.subtract(dir);
      this.clampVector();
      container.setScale(1);
      redraw(false);
    };

    container.on(Phaser.Input.Events.POINTER_DOWN, press);
    container.on(Phaser.Input.Events.POINTER_UP, release);
    container.on(Phaser.Input.Events.POINTER_OUT, release);
    return btn;
  }

  /** Clamp each axis into [-1,1] so a released-outside never leaves a stuck axis. */
  private clampVector(): void {
    this.vector.x = Phaser.Math.Clamp(this.vector.x, -1, 1);
    this.vector.y = Phaser.Math.Clamp(this.vector.y, -1, 1);
  }

  /** Reset every button to released + zero the vector (pointer-up-outside safety). */
  private releaseAll(): void {
    for (const b of this.buttons) {
      if (b.pressed) {
        b.pressed = false;
        b.container.setScale(1);
        b.redraw(false);
      }
    }
    this.vector.set(0, 0);
  }

  /**
   * Seat the pad as a bottom-corner cross within the given rect, thumb-reachable.
   * Bottom-left by default (Route); a scene whose bottom-left is already taken (Town's
   * menu toggle) can request 'bottom-right'. Idempotent — call from relayout() so a
   * resize/rotation re-anchors it.
   */
  layout(safe: SafeRect, corner: 'bottom-left' | 'bottom-right' = 'bottom-left'): void {
    const size = BTN_SIZE;
    const gap = SPACING.xs;
    const padCx =
      corner === 'bottom-right'
        ? safe.x + safe.width - size * 1.5 - SPACING.md - gap
        : safe.x + size * 1.5 + SPACING.md + gap;
    const padCy = safe.y + safe.height - size * 1.5 - SPACING.md - gap;
    // buttons were built up, left, right, down.
    const positions = [
      { x: padCx, y: padCy - (size + gap) }, // up
      { x: padCx - (size + gap), y: padCy }, // left
      { x: padCx + (size + gap), y: padCy }, // right
      { x: padCx, y: padCy + (size + gap) }, // down
    ];
    this.buttons.forEach((b, i) => {
      const p = positions[i];
      if (p) b.container.setPosition(p.x, p.y);
    });
  }

  /** Show/hide the whole pad (and zero the vector when hidden so nothing sticks). */
  setVisible(visible: boolean): this {
    this.visible = visible;
    for (const b of this.buttons) b.container.setVisible(visible);
    if (!visible) this.releaseAll();
    return this;
  }

  get isVisible(): boolean {
    return this.visible;
  }

  /** Drop input listeners + destroy the buttons (call from the scene's SHUTDOWN). */
  destroy(): void {
    this.scene.input?.off(Phaser.Input.Events.POINTER_UP_OUTSIDE, this.zeroMove);
    for (const b of this.buttons) b.container.destroy();
    this.buttons.length = 0;
    this.vector.set(0, 0);
  }
}
